/**
 * Each mount's own permissions (kernel apps, WS-14 section 3.6; layered shell surfaces, WS-8
 * section 3.10, overview section 7, R-22), moved from the shell's `services/permissions-service.ts`.
 *
 * A mount's `ShellPermissions` carries that mount's own registry `permissionKeys` and never
 * another app's. This replaces `activeAppPermissionKeys`' slug comparison, which closed the
 * cross-app leak by comparing two values; here the leak cannot arise, because each mount builds
 * from its own request.
 */
import { buildShellPermissions, type ShellPermissions } from '@zion/sdk-contracts';
import type { SurfacePermissionsService } from '@zion/sdk-surfaces/v1';
import { createListenerSet, type MountScope, type ServiceSession } from './kit.js';

/**
 * One mount's permissions and the host's call to replace its keys.
 *
 * - `service.current()` returns `buildShellPermissions(orgRoles, orgPermissions, keys)` while the
 *   session is authenticated. Hydrating or anonymous, every argument is `[]`, so every predicate
 *   answers `false`. The object keeps its identity until the roles, the permissions, the status
 *   or the keys change; then it is rebuilt and each subscriber hears once.
 * - `updateKeys(keys)` is the host's call after a registry refetch (`SurfaceServicesHost`
 *   `updatePermissionKeys`). It is absent from `SurfaceServices`, so a surface cannot widen its
 *   own keys.
 * - The session subscription and every subscriber end with the mount.
 */
export interface MountPermissions {
  readonly service: SurfacePermissionsService;
  updateKeys(keys: readonly string[]): void;
}

function sameList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((item, i) => item === b[i]);
}

interface PermissionInputs {
  readonly roles: readonly string[];
  readonly permissions: readonly string[];
  readonly keys: readonly string[];
}

/** Build one mount's permissions from its request's `permissionKeys` and the session. */
export function createMountPermissions(scope: MountScope, session: ServiceSession): MountPermissions {
  const listeners = createListenerSet();
  let keys: readonly string[] = [...scope.request.permissionKeys];

  const inputsNow = (): PermissionInputs => {
    const snapshot = session.current();
    return snapshot.status === 'authenticated'
      ? { roles: [...snapshot.orgRoles], permissions: [...snapshot.orgPermissions], keys }
      : { roles: [], permissions: [], keys: [] };
  };
  let inputs = inputsNow();
  let value: ShellPermissions = buildShellPermissions(inputs.roles, inputs.permissions, inputs.keys);

  const refresh = (): void => {
    const next = inputsNow();
    if (
      sameList(next.roles, inputs.roles) &&
      sameList(next.permissions, inputs.permissions) &&
      sameList(next.keys, inputs.keys)
    ) {
      return;
    }
    inputs = next;
    value = buildShellPermissions(next.roles, next.permissions, next.keys);
    listeners.notify();
  };

  scope.own(session.subscribe(refresh));
  scope.own(() => listeners.clear());

  return {
    service: scope.guard({
      current: () => value,
      subscribe: (listener: () => void) => listeners.add(listener),
    }),
    updateKeys(next) {
      if (scope.disposed) return;
      keys = [...next];
      refresh();
    },
  };
}
